import React, { useState, useEffect } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { clientService } from "../Client.service";
import { Client, ClientLink } from "@/types";
import { apiClient } from "@/services/apiClient";
import { ClientSharingPanel } from "./ClientSharingPanel";
import { CloseIcon, SpinnerIcon, ErrorIcon, PlusIcon, RefreshIcon } from "@/components/icons";
import { buildProfileUpdate, ProfileFormValues } from "./clientFormFields";

interface EditClientProfileModalProps {
  isOpen: boolean;
  client: Client | null;
  onClose: () => void;
  onClientUpdated: (client: Client) => void;
}

type ClientStatus = "ACTIVE" | "PROPOSAL" | "ARCHIVED";

/**
 * Client Profile form: who the client is. Identity and descriptive fields
 * (name, status, language, sector, location, relationship, follower baseline,
 * links). Operational config lives in EditClientSettingsModal. Status routes
 * through updateClientStatus; the rest diff through clientService.updateClient.
 */
export const EditClientProfileModal: React.FC<EditClientProfileModalProps> = ({
  isOpen,
  client,
  onClose,
  onClientUpdated,
}) => {
  const [clientName, setClientName] = useState("");
  const [status, setStatus] = useState<ClientStatus>("ACTIVE");
  const [instagramStartFollowers, setInstagramStartFollowers] = useState("");
  const [facebookStartFollowers, setFacebookStartFollowers] = useState("");
  const [relationshipStartedAt, setRelationshipStartedAt] = useState("");
  const [locale, setLocale] = useState("");
  const [sector, setSector] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [location, setLocation] = useState("");
  const [links, setLinks] = useState<ClientLink[]>([]);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (client) {
      setClientName(client.clientName);
      setStatus((client.status as ClientStatus) || "ACTIVE");
      setInstagramStartFollowers(client.instagramStartFollowers?.toString() || "");
      setFacebookStartFollowers(client.facebookStartFollowers?.toString() || "");
      setRelationshipStartedAt(client.relationshipStartedAt || "");
      setLocale(client.locale || "");
      setSector(client.sector || "");
      setLatitude(client.latitude?.toString() || "");
      setLongitude(client.longitude?.toString() || "");
      setLocation(client.location || "");
      setLinks(client.links || []);
      setWebsiteUrl(client.websiteUrl || "");
      setSyncResult(null);
      setError(null);
    }
  }, [client]);

  const handleAddLink = () => {
    setLinks([...links, { name: "", url: "" }]);
  };

  const handleRemoveLink = (index: number) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  const handleLinkChange = (index: number, field: keyof ClientLink, value: string) => {
    const updatedLinks = [...links];
    updatedLinks[index] = { ...updatedLinks[index], [field]: value };
    setLinks(updatedLinks);
  };

  const handleSyncHistoricalReports = async () => {
    if (!client || !relationshipStartedAt) return;

    setIsSyncing(true);
    setSyncResult(null);
    setError(null);

    try {
      const response = await apiClient.post<{
        success: boolean;
        status: string;
        sync_id: string;
      }>(`/api/social-media/admin/sync-historical-reports/${client.id}`);

      if (response.success) {
        setSyncResult({
          success: true,
          message: "Sync started. You'll be notified when it completes.",
        });
      } else {
        setSyncResult({ success: false, message: "Failed to start sync" });
      }
    } catch (err) {
      console.error("Error starting sync:", err);
      setSyncResult({
        success: false,
        message: err instanceof Error ? err.message : "Failed to start sync",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!client) return;

    if (!clientName.trim()) {
      setError("Client name is required");
      return;
    }

    const validLinks = links.filter((link) => link.name.trim() && link.url.trim());
    for (const link of validLinks) {
      try {
        new URL(link.url);
      } catch {
        setError(`Invalid URL for "${link.name}": ${link.url}`);
        return;
      }
    }

    const trimmedWebsite = websiteUrl.trim();
    if (trimmedWebsite) {
      try {
        new URL(trimmedWebsite);
      } catch {
        setError(`Invalid website URL: ${trimmedWebsite}`);
        return;
      }
    }

    setIsSaving(true);
    setError(null);

    try {
      // Status uses a dedicated endpoint.
      const currentStatus = (client.status as ClientStatus) || "ACTIVE";
      if (status !== currentStatus) {
        await clientService.updateClientStatus(client.id, status);
      }

      const values: ProfileFormValues = {
        clientName,
        locale,
        sector,
        location,
        latitude,
        longitude,
        relationshipStartedAt,
        instagramStartFollowers,
        facebookStartFollowers,
        links: validLinks,
        websiteUrl: trimmedWebsite,
      };
      const updateData = buildProfileUpdate(values, client);

      if (Object.keys(updateData).length > 0) {
        await clientService.updateClient(client.id, updateData);
      }

      const updatedClient = await clientService.getClientById(client.id);
      onClientUpdated(updatedClient);
      onClose();
    } catch (err) {
      console.error("Error updating client:", err);
      setError(err instanceof Error ? err.message : "Failed to update client");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving && !isSyncing) {
      setError(null);
      setSyncResult(null);
      onClose();
    }
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isSaving}>
        Cancel
      </Button>
      <Button
        type="submit"
        form="edit-client-profile-form"
        disabled={isSaving || !clientName.trim()}
        className="flex items-center gap-2"
      >
        {isSaving ? (
          <>
            <SpinnerIcon className="w-4 h-4" />
            Saving...
          </>
        ) : (
          "Save Changes"
        )}
      </Button>
    </>
  );

  if (!client) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Client Profile"
      size="lg"
      footer={footerContent}
    >
      <form id="edit-client-profile-form" onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Name and Status */}
        <div className="grid grid-cols-[1fr_auto] gap-4 items-start">
          <div>
            <label htmlFor="profileClientName" className="block text-sm font-medium text-gray-700 mb-2">
              Client Name
            </label>
            <input
              id="profileClientName"
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Enter client name..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
              disabled={isSaving}
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="profileStatus" className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              id="profileStatus"
              value={status}
              onChange={(e) => setStatus(e.target.value as ClientStatus)}
              className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors ${
                status === "ARCHIVED"
                  ? "border-gray-300 bg-gray-50 text-gray-600"
                  : status === "PROPOSAL"
                    ? "border-amber-300 bg-amber-50 text-amber-700"
                    : "border-green-300 bg-green-50 text-green-700"
              }`}
              disabled={isSaving}
            >
              <option value="ACTIVE">Active</option>
              <option value="PROPOSAL">Proposal</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>
        </div>

        {/* Identity */}
        <fieldset className="space-y-4">
          <legend className="text-sm font-medium text-gray-900">Identity</legend>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="profileLocale" className="block text-sm font-medium text-gray-700 mb-1">
                Language
              </label>
              <select
                id="profileLocale"
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
                disabled={isSaving}
              >
                <option value="">Auto-detect</option>
                <option value="en">English</option>
                <option value="it">Italian</option>
                <option value="ro">Romanian</option>
                <option value="de">German</option>
                <option value="fr">French</option>
                <option value="es">Spanish</option>
                <option value="pt">Portuguese</option>
              </select>
            </div>
            <div>
              <label htmlFor="profileSector" className="block text-sm font-medium text-gray-700 mb-1">
                Sector
              </label>
              <input
                id="profileSector"
                type="text"
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                placeholder="e.g. hospitality, health_beauty, real_estate, wellness, food_drink, retail"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
                disabled={isSaving}
              />
            </div>
          </div>
        </fieldset>

        {/* Location */}
        <fieldset className="space-y-4">
          <legend className="text-sm font-medium text-gray-900">Location</legend>
          <div>
            <label htmlFor="profileLocation" className="block text-sm font-medium text-gray-700 mb-1">
              Location label
            </label>
            <input
              id="profileLocation"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Temple Bar, Dublin"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
              disabled={isSaving}
            />
            <p className="text-xs text-gray-500 mt-1">Human-readable location used by pulse, calendar, and ads targeting</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="profileLatitude" className="block text-sm font-medium text-gray-700 mb-1">
                Latitude
              </label>
              <input
                id="profileLatitude"
                type="number"
                step="any"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder="e.g. 41.9028"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
                disabled={isSaving}
              />
            </div>
            <div>
              <label htmlFor="profileLongitude" className="block text-sm font-medium text-gray-700 mb-1">
                Longitude
              </label>
              <input
                id="profileLongitude"
                type="number"
                step="any"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder="e.g. 12.4964"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
                disabled={isSaving}
              />
            </div>
          </div>
        </fieldset>

        {/* Relationship */}
        <fieldset className="space-y-4">
          <legend className="text-sm font-medium text-gray-900">Relationship</legend>
          <div>
            <label htmlFor="profileRelationshipStartedAt" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <div className="flex gap-2">
              <input
                id="profileRelationshipStartedAt"
                type="date"
                value={relationshipStartedAt}
                onChange={(e) => {
                  setRelationshipStartedAt(e.target.value);
                  setSyncResult(null);
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
                disabled={isSaving || isSyncing}
              />
              <button
                type="button"
                onClick={handleSyncHistoricalReports}
                disabled={!relationshipStartedAt || isSaving || isSyncing}
                className="px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center gap-1.5"
                title={!relationshipStartedAt ? "Set a start date first" : "Backfill reports from start date"}
              >
                {isSyncing ? (
                  <>
                    <SpinnerIcon className="w-3.5 h-3.5" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshIcon className="w-3.5 h-3.5" />
                    Sync Reports
                  </>
                )}
              </button>
            </div>
            {syncResult && (
              <p className={`text-xs mt-1 ${syncResult.success ? "text-green-600" : "text-red-600"}`}>
                {syncResult.message}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="profileInstagramStartFollowers" className="block text-sm font-medium text-gray-700 mb-1">
                Instagram Start Followers
              </label>
              <input
                id="profileInstagramStartFollowers"
                type="number"
                value={instagramStartFollowers}
                onChange={(e) => setInstagramStartFollowers(e.target.value)}
                placeholder="e.g. 1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
                disabled={isSaving}
                min="0"
              />
            </div>
            <div>
              <label htmlFor="profileFacebookStartFollowers" className="block text-sm font-medium text-gray-700 mb-1">
                Facebook Start Followers
              </label>
              <input
                id="profileFacebookStartFollowers"
                type="number"
                value={facebookStartFollowers}
                onChange={(e) => setFacebookStartFollowers(e.target.value)}
                placeholder="e.g. 500"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
                disabled={isSaving}
                min="0"
              />
            </div>
          </div>
        </fieldset>

        {/* Website */}
        <fieldset className="space-y-4">
          <legend className="text-sm font-medium text-gray-900">Website</legend>
          <div>
            <input
              id="profileWebsiteUrl"
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
              disabled={isSaving}
            />
            <p className="text-xs text-gray-500 mt-1">
              The client's own site. Traffic ad campaigns send people here — they can't run without it.
            </p>
          </div>
        </fieldset>

        {/* Links */}
        <fieldset className="space-y-3">
          <div className="flex items-center justify-between">
            <legend className="text-sm font-medium text-gray-900">Links</legend>
            <button
              type="button"
              onClick={handleAddLink}
              disabled={isSaving}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Add Link
            </button>
          </div>

          {links.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No links added yet</p>
          ) : (
            <div className="space-y-3">
              {links.map((link, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <input
                    type="text"
                    value={link.name}
                    onChange={(e) => handleLinkChange(index, "name", e.target.value)}
                    placeholder="Name (e.g., Instagram)"
                    className="w-1/3 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors text-sm"
                    disabled={isSaving}
                  />
                  <input
                    type="url"
                    value={link.url}
                    onChange={(e) => handleLinkChange(index, "url", e.target.value)}
                    placeholder="https://..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors text-sm"
                    disabled={isSaving}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveLink(index)}
                    disabled={isSaving}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove link"
                  >
                    <CloseIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </fieldset>

        {/* Sharing */}
        <div className="border-t pt-4">
          <ClientSharingPanel clientId={client.id} />
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <ErrorIcon className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <div className="text-red-700 text-sm">{error}</div>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
};
